import { useEffect, useMemo, useState } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import record from "@/data/track-record.json";

export const formatR = (value: number) => `${value >= 0 ? "+" : "-"}${Math.abs(value).toFixed(2)}R`;

type MonthlyPoint = {
  key: string;
  period: string;
  shortPeriod: string;
  year: number;
  netR: number;
  cumulativeR: number;
  annualShare: number;
  trades: number;
  winRate: number;
};

const monthlyPoints: MonthlyPoint[] = [];
let runningTotal = 0;
for (const year of record.years) {
  for (const month of year.months) {
    runningTotal += month.netR;
    monthlyPoints.push({
      key: `${year.year}-${String(month.month + 1).padStart(2, "0")}`,
      period: `${month.label} ${year.year}`,
      shortPeriod: month.label,
      year: year.year,
      netR: month.netR,
      cumulativeR: Number(runningTotal.toFixed(2)),
      annualShare: year.overall.netR === 0 ? 0 : (month.netR / year.overall.netR) * 100,
      trades: month.trades,
      winRate: month.winRate,
    });
  }
}

function TrajectoryTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{ payload: MonthlyPoint }>;
}) {
  const point = payload?.[0]?.payload;
  if (!active || !point) return null;

  const resultTone = point.netR > 0 ? "text-emerald" : point.netR < 0 ? "text-destructive" : "text-foreground";
  return (
    <div className="min-w-44 rounded-md border border-gold/35 bg-popover/95 p-3 shadow-elevated backdrop-blur-md">
      <div className="flex items-center justify-between gap-4 border-b border-border/50 pb-2">
        <span className="font-mono text-[9px] uppercase tracking-[0.16em] text-muted-foreground">{point.period}</span>
        <span className="font-mono text-[8px] uppercase tracking-[0.14em] text-gold">Verified</span>
      </div>
      <div className="mt-2 flex items-end justify-between gap-5">
        <div>
          <div className="font-mono text-[8px] uppercase tracking-wider text-muted-foreground">Monthly net</div>
          <div className={`mt-0.5 font-mono text-lg font-semibold tabular-nums ${resultTone}`}>{formatR(point.netR)}</div>
        </div>
        <div className="text-right">
          <div className="font-mono text-[8px] uppercase tracking-wider text-muted-foreground">Share of year</div>
          <div className="mt-0.5 font-mono text-sm tabular-nums text-gold">{point.annualShare.toFixed(1)}%</div>
        </div>
      </div>
      <div className="mt-2 flex justify-between border-t border-border/40 pt-2 font-mono text-[9px] text-muted-foreground">
        <span>{point.trades} positions</span>
        <span>{point.winRate.toFixed(1)}% win rate</span>
      </div>
    </div>
  );
}

export function MonthlyPerformanceTrajectory({ compact = false }: { compact?: boolean }) {
  const [allowAnimation, setAllowAnimation] = useState(false);
  const total = monthlyPoints.at(-1)?.cumulativeR ?? 0;
  const finalPoint = monthlyPoints.at(-1);
  const yearStarts = useMemo(() => monthlyPoints.filter((point) => point.shortPeriod === "Jan" && point.year > 2024), []);

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    setAllowAnimation(!media.matches);
  }, []);

  return (
    <figure className="overflow-hidden rounded-lg border border-border/60 bg-background/45 shadow-elevated">
      <figcaption className="flex flex-wrap items-end justify-between gap-4 border-b border-border/50 px-4 py-4 sm:px-5">
        <div>
          <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-muted-foreground">Verified monthly trajectory</div>
          <h3 className="mt-1 font-display text-lg">34 Months of Recorded Performance</h3>
        </div>
        <div className="text-left sm:text-right">
          <div className="font-mono text-[9px] uppercase tracking-wider text-muted-foreground">Cumulative net return</div>
          <div className="font-mono text-2xl font-semibold tabular-nums text-gold">{formatR(total)}</div>
        </div>
      </figcaption>

      <div className={`relative w-full px-1 pt-4 sm:px-3 ${compact ? "h-64" : "h-80"}`}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={monthlyPoints} margin={{ top: 18, right: 18, bottom: 10, left: 2 }}>
            <defs>
              <linearGradient id="verifiedMonthlyPerformanceFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="var(--gold)" stopOpacity={0.3} />
                <stop offset="72%" stopColor="var(--gold)" stopOpacity={0.06} />
                <stop offset="100%" stopColor="var(--gold)" stopOpacity={0} />
              </linearGradient>
              <filter id="verifiedMonthlyPerformanceGlow" x="-20%" y="-20%" width="140%" height="140%">
                <feGaussianBlur stdDeviation="2" result="blur" />
                <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
              </filter>
            </defs>
            <CartesianGrid vertical={false} stroke="var(--border)" strokeOpacity={0.35} strokeDasharray="4 8" />
            <XAxis
              dataKey="key"
              axisLine={false}
              tickLine={false}
              minTickGap={28}
              tick={{ fill: "var(--muted-foreground)", fontSize: 9, fontFamily: "var(--font-mono)" }}
              tickFormatter={(value: string) => {
                const point = monthlyPoints.find((candidate) => candidate.key === value);
                return point?.shortPeriod === "Jan" ? String(point.year) : point?.shortPeriod ?? "";
              }}
            />
            <YAxis
              width={42}
              axisLine={false}
              tickLine={false}
              tick={{ fill: "var(--muted-foreground)", fontSize: 9, fontFamily: "var(--font-mono)" }}
              tickFormatter={(value: number) => `${value}R`}
            />
            {yearStarts.map((point) => (
              <ReferenceLine key={point.key} x={point.key} stroke="var(--border)" strokeOpacity={0.7} strokeDasharray="3 7" />
            ))}
            <Tooltip
              content={<TrajectoryTooltip />}
              cursor={{ stroke: "var(--gold)", strokeOpacity: 0.45, strokeWidth: 1 }}
              allowEscapeViewBox={{ x: false, y: false }}
              wrapperStyle={{ outline: "none", zIndex: 20 }}
            />
            <Area
              type="monotone"
              dataKey="cumulativeR"
              stroke="var(--gold)"
              strokeWidth={2.5}
              fill="url(#verifiedMonthlyPerformanceFill)"
              filter="url(#verifiedMonthlyPerformanceGlow)"
              isAnimationActive={allowAnimation}
              animationDuration={1100}
              animationEasing="ease-out"
              dot={false}
              activeDot={{ r: 5, fill: "var(--background)", stroke: "var(--gold)", strokeWidth: 3 }}
            />
          </AreaChart>
        </ResponsiveContainer>
        {finalPoint ? (
          <div aria-hidden className="pointer-events-none absolute right-[1.55rem] top-[1.85rem] hidden h-3 w-3 rounded-full bg-gold/50 animate-pulse motion-reduce:animate-none sm:block" />
        ) : null}
      </div>

      <div className="grid grid-cols-3 gap-px border-t border-border/50 bg-border/40 font-mono text-[9px] uppercase tracking-[0.12em] text-muted-foreground">
        {record.years.map((year) => (
          <div key={year.year} className="bg-background/80 px-3 py-2.5 text-center">
            {year.year === 2026 ? "Jan to Sep 2026" : `Jan to Dec ${year.year}`}
          </div>
        ))}
      </div>
    </figure>
  );
}
